"use client";

import { useRef } from "react";
import CustomerLogos from "../CustomerLogos";
import { GridRowCenter, PINK_BAND, SectionShell, useBandRowAlign, useRefGridMode, useWholeRowBlocks } from "./kit";
import { useCountUp } from "./hooks/useCountUp";
import { useInViewOnce } from "./hooks/useInViewOnce";

interface Stat {
  to: number;
  decimals: number;
  suffix: string;
  label: string;
}

// Product facts only (plans.py PLAN_DEFAULTS[FREE]) plus
// the owner-confirmed Enterprise SLA (2026-09-27). No usage totals or latency
// figures until they are measured and published — the old 2.4B links / 38B
// clicks / 11 ms were placeholders.
const STATS: Stat[] = [
  { to: 100, decimals: 0, suffix: "", label: "Active links on Free" },
  { to: 2, decimals: 0, suffix: "", label: "Custom domains on Free" },
  { to: 60, decimals: 0, suffix: "/min", label: "API requests on Free" },
  { to: 99.99, decimals: 2, suffix: "%", label: "Uptime SLA on Enterprise" },
];

/** `light` = the drenched pink homepage band (light content on brand pink). */
function StatValue({ stat, active, light = false }: { stat: Stat; active: boolean; light?: boolean }) {
  const value = useCountUp(stat.to, active);
  return (
    <>
      <div
        className={`text-[clamp(34px,4vw,52px)] font-bold leading-none tracking-[-0.03em] tabular-nums ${
          light ? "text-white" : ""
        }`}
      >
        {value.toFixed(stat.decimals)}
        <span className={light ? "text-white/70" : "text-brand-600"}>{stat.suffix}</span>
      </div>
      <div className={`mt-3 text-sm ${light ? "text-white/90" : "text-muted"}`}>{stat.label}</div>
    </>
  );
}

function Logos({ light = false }: { light?: boolean }) {
  return (
    <>
      <span
        className={`trust-logos__label text-xs uppercase tracking-[0.15em] ${light ? "text-white/80" : "text-dim"}`}
      >
        Running links for teams like
      </span>
      <CustomerLogos light={light} />
    </>
  );
}

/** Trust logos + animated stat bar. Band surface by default; on the homepage
 *  grid it becomes the drenched brand-pink band — light content on pink, the
 *  grid repainted in light hairlines. */
export default function TrustStatsSection() {
  const onGrid = useRefGridMode();
  const [ref, inView] = useInViewOnce<HTMLDivElement>(0.4);
  const rootRef = useRef<HTMLDivElement>(null);
  const nullRef = useRef<HTMLDivElement>(null); // never attached — disables the hooks off-grid

  // useWholeRowBlocks/useBandRowAlign pad and re-align [data-grid-block]s for
  // grid-paper layout — running them on /security, /solutions/enterprise or
  // /about (non-grid RefSections) would inject inline padding/margins into
  // this SHARED section and break "visually unchanged". Hooks can't be called
  // conditionally, so gate them by ref instead: both bail immediately once
  // `innerRef.current` is null, so pointing them at `nullRef` (never
  // attached) off-grid is a full no-op rather than a skipped hook call.
  useWholeRowBlocks(onGrid ? rootRef : nullRef);
  useBandRowAlign(onGrid ? rootRef : nullRef);

  if (onGrid) {
    return (
      // RowBand is md+ only — below md the section itself paints the pink so
      // the light content never sits on the light page background.
      <SectionShell variant="tight" band="pink" decoAvoid className="max-md:bg-[#d70e62]">
        <div ref={rootRef}>
          {/* Row-snapped so the logo row sits vertically centered in a grid row. */}
          <GridRowCenter className="mb-[46px]">
            {/* `data-snug`: GridRowCenter already row-snaps this floater to a
                single whole cell (height + top-line margin on its own
                wrapper) — the default +1 breathing row would double that
                growth when useWholeRowBlocks pads this block on top of it. */}
            <div
              data-grid-block
              data-grid-fill={PINK_BAND}
              data-snug
              className="trust-logos mx-auto w-full"
            >
              <Logos light />
            </div>
          </GridRowCenter>

          <div
            ref={ref}
            data-grid-block
            data-grid-fill={PINK_BAND}
            className="grid grid-cols-2 md:grid-cols-4"
          >
            {STATS.map((stat, i) => (
              <div
                key={stat.label}
                className={`px-7 py-[38px] text-center md:border-r md:border-white/25 ${
                  i === STATS.length - 1 ? "md:border-r-0" : ""
                }`}
              >
                <StatValue stat={stat} active={inView} light />
              </div>
            ))}
          </div>
        </div>
      </SectionShell>
    );
  }

  return (
    <section className="border-y border-line bg-bg-warm-2">
      <div className="mx-auto w-full max-w-[1200px] px-7">
        <div
          ref={rootRef}
          className="trust-logos py-[30px]"
        >
          <Logos />
        </div>
      </div>

      <div
        ref={ref}
        className="mx-auto grid w-full max-w-[1200px] grid-cols-2 border-t border-line px-7 md:grid-cols-4 md:px-0"
      >
        {STATS.map((stat, i) => (
          <div
            key={stat.label}
            className={`px-7 py-[38px] text-center md:border-r md:border-line ${
              i === STATS.length - 1 ? "md:border-r-0" : ""
            }`}
          >
            <StatValue stat={stat} active={inView} />
          </div>
        ))}
      </div>
    </section>
  );
}
